import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import { openModal } from '../../../store/slices/uiSlice';
import useFlightSearch from '../../../hooks/useFlightSearch';
import airportService from '../../../services/airportService';
import { navigateWithBookingData } from '../../../utils/bookingQueryParams';

export const useFlightSelection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const searchParams = new URLSearchParams(location.search);

  // Get authentication state
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  // Flight search hook
  const {
    loading: searchLoading,
    error: searchError,
    searchFlights: performSearch,
    getFlightDetails,
    filterFlights,
    sortFlights
  } = useFlightSearch();

  // Local state
  const [outboundFlights, setOutboundFlights] = useState([]);
  const [returnFlights, setReturnFlights] = useState([]);
  const [multiCitySegments, setMultiCitySegments] = useState([]);
  const [activeSegmentIndex, setActiveSegmentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedFlights, setSelectedFlights] = useState([]);
  const [activeFlightTab, setActiveFlightTab] = useState('outbound');
  const [filters, setFilters] = useState({
    lowestPax: false,
    fastestFlightTime: false,
    morning: false,
    afternoon: false,
    evening: false,
    sortBy: 'default'
  });
  const [departureAirport, setDepartureAirport] = useState(null);
  const [arrivalAirport, setArrivalAirport] = useState(null);
  const [enrichedSegments, setEnrichedSegments] = useState([]);

  // Determine flight type and mode based on URL path
  const getFlightConfig = () => {
    const path = location.pathname;
    let flightType = 'one-way';
    let mode = 'search';

    if (path.includes('one-way')) flightType = 'one-way';
    else if (path.includes('return')) flightType = 'return';
    else if (path.includes('multi-city')) flightType = 'multi-city';
    else if (path.includes('flight-list')) flightType = 'one-way';

    if (path.includes('search') || path.includes('list') || path.includes('flight')) {
      mode = location.state?.selectedFlights ? 'details' : 'search';
    }

    return { flightType, mode };
  };

  const { flightType, mode } = getFlightConfig();
  const totalPassengers = parseInt(searchParams.get('passengers') || '2');

  // Get search parameters with defaults
  const getSearchData = () => {
    const tripType = searchParams.get('tripType') || flightType;

    if (flightType === 'multi-city') {
      const segmentCount = parseInt(searchParams.get('segmentCount') || '2');
      const segments = [];

      for (let i = 1; i <= segmentCount; i++) {
        const from = searchParams.get(`from${i}`);
        const to = searchParams.get(`to${i}`);
        const date = searchParams.get(`date${i}`);

        if (from && to && date) {
          segments.push({ from, to, date, segmentNumber: i });
        }
      }

      return {
        segments,
        passengers: totalPassengers,
        tripType,
        booking_token: searchParams.get('booking_token')
      };
    }

    return {
      from: searchParams.get('from') || (flightType === 'return' ? 'AAR' : 'AAL'),
      to: searchParams.get('to') || (flightType === 'return' ? 'TAB' : 'TAB'),
      departure: searchParams.get('departure') || 'Fri 31 Jan 26',
      return: searchParams.get('return') || 'Sat 31 Jan 26',
      passengers: totalPassengers,
      tripType,
      booking_token: searchParams.get('booking_token')
    };
  };

  const searchData = getSearchData();

  // Fetch airport names/codes based on IDs
  useEffect(() => {
    const fetchAirportDetails = async () => {
      const idsToFetch = new Set();

      if (flightType === 'multi-city') {
        searchData.segments.forEach(seg => {
          if (seg.from && !isNaN(parseInt(seg.from))) idsToFetch.add(parseInt(seg.from));
          if (seg.to && !isNaN(parseInt(seg.to))) idsToFetch.add(parseInt(seg.to));
        });
      } else {
        if (searchData.from && !isNaN(parseInt(searchData.from))) idsToFetch.add(parseInt(searchData.from));
        if (searchData.to && !isNaN(parseInt(searchData.to))) idsToFetch.add(parseInt(searchData.to));
      }

      if (idsToFetch.size === 0) return;

      try {
        const response = await airportService.getAirportsByIds(Array.from(idsToFetch));
        const airportMap = {};
        if (response && response.data) {
          response.data.forEach(airport => {
            airportMap[airport.id] = airport;
          });
        }

        if (flightType === 'multi-city') {
          const enriched = searchData.segments.map(seg => ({
            ...seg,
            fromAirport: airportMap[seg.from] || { airport_name: seg.from, iata: '' },
            toAirport: airportMap[seg.to] || { airport_name: seg.to, iata: '' }
          }));
          setEnrichedSegments(enriched);
        } else {
          setDepartureAirport(airportMap[searchData.from]);
          setArrivalAirport(airportMap[searchData.to]);
        }
      } catch (err) {
        console.error('Failed to fetch airport details:', err);
      }
    };

    fetchAirportDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, flightType]);

  // Fetch flights based on URL parameters or state
  useEffect(() => {
    console.log('useFlightSelection: Fetching flights', {
      hasState: !!location.state,
      mode,
      searchParams: location.search
    });

    // Check if we have selected flights from previous step (details mode)
    if (location.state?.selectedFlights) {
      console.log('Using selected flights from state');
      setSelectedFlights(location.state.selectedFlights);
      setLoading(false);
      return;
    }

    // Use cached search results if available
    if (location.state?.searchResults && location.state?.fromSearch) {
      const results = location.state.searchResults;
      setOutboundFlights(results.outboundFlights || []);
      setReturnFlights(results.returnFlights || []);
      setMultiCitySegments(results.segments || []);
      setLoading(false);
      return;
    }

    if (mode === 'details' && !location.state?.selectedFlights) {
      const fetchFlight = async () => {
        const id = location.pathname.split('/').pop();
        const details = await getFlightDetails(id);
        setSelectedFlights([details]);
        setLoading(false);
      };
      fetchFlight();
      return;
    }

    // Perform search based on URL parameters
    const performSearchFromParams = async () => {
      const searchData = {
        from: searchParams.get('from'),
        to: searchParams.get('to'),
        departure: searchParams.get('departure'),
        return: searchParams.get('return'),
        passengers: parseInt(searchParams.get('passengers') || '1'),
        tripType: searchParams.get('tripType') || 'One Way',
        nearby: searchParams.get('nearby') === 'true',
        booking_token: searchParams.get('booking_token')
      };

      // Handle multi-city search
      if (searchData.tripType && (searchData.tripType.toLowerCase() === 'multicity' || searchData.tripType.toLowerCase() === 'multi-city')) {
        const segmentCount = parseInt(searchParams.get('segmentCount') || '2');
        const segments = [];

        for (let i = 1; i <= segmentCount; i++) {
          const from = searchParams.get(`from${i}`);
          const to = searchParams.get(`to${i}`);
          const date = searchParams.get(`date${i}`);

          if (from && to && date) {
            segments.push({ from, to, date, segmentNumber: i });
          }
        }

        searchData.segments = segments;
      }

      // Only search if we have required parameters
      const isMultiCityTrip = searchData.tripType && searchData.tripType.toLowerCase().includes('multi');

      if (isMultiCityTrip) {
        if (searchData.segments && searchData.segments.length >= 2) {
          try {
            setLoading(true);
            const results = await performSearch(searchData);
            if (results && results.segments) {
              setMultiCitySegments(results.segments);
            } else {
              setMultiCitySegments([]);
            }
            setActiveSegmentIndex(0);
          } catch (error) {
            setMultiCitySegments([]);
          } finally {
            setLoading(false);
          }
        }
      } else if (searchData.from && searchData.to && searchData.departure) {
        try {
          setLoading(true);
          const results = await performSearch(searchData);
          setOutboundFlights(results.outboundFlights || []);
          setReturnFlights(results.returnFlights || []);
        } catch (error) {
          setOutboundFlights([]);
          setReturnFlights([]);
        } finally {
          setLoading(false);
        }
      }
    };

    performSearchFromParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.state]);

  // Get filtered and sorted flights
  const getDisplayFlights = () => {
    let displayFlights = [];

    if (flightType === 'multi-city' && mode === 'search') {
      const activeSegment = multiCitySegments[activeSegmentIndex];
      displayFlights = activeSegment?.flights || [];
    } else if (flightType === 'return' && mode === 'search') {
      displayFlights = activeFlightTab === 'outbound' ? outboundFlights : returnFlights;
    } else {
      displayFlights = outboundFlights;
    }

    // Apply time of day filter
    displayFlights = filterFlights(displayFlights, filters);

    // Determine effective sort — lowestPax/fastestFlightTime checkboxes map to sort options
    let effectiveSort = filters.sortBy;
    if (filters.lowestPax) effectiveSort = 'price_asc';
    if (filters.fastestFlightTime) effectiveSort = 'duration_asc';

    displayFlights = sortFlights(displayFlights, effectiveSort);

    return displayFlights;
  };

  // Handle flight selection
  const handleFlightSelect = async (flight) => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      sessionStorage.setItem('redirectAfterLogin', location.pathname + location.search);
      dispatch(openModal('login'));
      return;
    }

    try {
      const flightDetails = await getFlightDetails(flight.id);
      const searchData = getSearchData();
      console.log("flight details selected", flightDetails)
      // For multi-city flights, handle sequential selection
      const isMultiCity = searchData.tripType && (searchData.tripType.toLowerCase() === 'multicity' || searchData.tripType.toLowerCase() === 'multi-city');

      if (isMultiCity) {
        const updatedSelectedFlights = [...selectedFlights];
        updatedSelectedFlights[activeSegmentIndex] = flightDetails;
        updatedSelectedFlights.length = activeSegmentIndex + 1;
        setSelectedFlights(updatedSelectedFlights);
        toast.success(`Flight ${activeSegmentIndex + 1} selected successfully`);


        if (activeSegmentIndex < multiCitySegments.length - 1) {
          const nextSegmentIndex = activeSegmentIndex + 1;
          console.log(`Moving to segment ${nextSegmentIndex + 1} of ${multiCitySegments.length}`);
          setActiveSegmentIndex(nextSegmentIndex);
          return;
        } else {
          // All segments selected - navigate to booking flow
          const bookingData = {
            flight_id: updatedSelectedFlights[0].id,
            aircraft_id: updatedSelectedFlights[0].aircraft_id,
            registration_number: updatedSelectedFlights[0].registration_number,
            flight_number: updatedSelectedFlights[0].flight_number,
            departure_airport: updatedSelectedFlights[0].departure_airport,
            departure_date_time: updatedSelectedFlights[0].departure?.datetime,
            arrival_airport: updatedSelectedFlights[0].arrival_airport,
            arrival_date_time: updatedSelectedFlights[0].arrival?.datetime,
            passengers: searchData.passengers || 1,
            routeType: 'Multicity',
            selectedFlights: updatedSelectedFlights
          };

          navigateWithBookingData(
            navigate,
            `/flight-booking-flow/${updatedSelectedFlights[0].id}`,
            bookingData,
            {
              state: {
                selectedFlight: updatedSelectedFlights[0],
                selectedFlights: updatedSelectedFlights,
                searchParams: searchData,
                fromUrl: location.pathname + location.search
              }
            }
          );
          return;
        }
      }

      // For return flights, handle selection based on active tab
      const isReturnFlight = searchData.tripType === 'Returns' || searchData.tripType === 'return';

      if (isReturnFlight) {
        if (activeFlightTab === 'outbound') {
          setSelectedFlights([flightDetails]);
          setActiveFlightTab('return');
          toast.success('Outbound flight selected. Please select your return flight.');
          return;
        } else {
          const existingOutbound = selectedFlights[0];
          if (!existingOutbound) {
            toast.error('Please select outbound flight first.');
            setActiveFlightTab('outbound');
            return;
          }

          const outboundId = existingOutbound.id;
          const returnId = flightDetails.id;

          const bookingData = {
            flight_id: outboundId,
            return_flight_id: returnId,
            aircraft_id: existingOutbound.aircraft_id,
            registration_number: existingOutbound.registration_number,
            flight_number: existingOutbound.flight_number,
            departure_airport: existingOutbound.departure_airport,
            departure_date_time: existingOutbound.departure?.datetime,
            arrival_airport: existingOutbound.arrival_airport,
            arrival_date_time: existingOutbound.arrival?.datetime,
            passengers: searchData.passengers || 1,
            routeType: searchData.tripType || 'Returns',
            from: searchData.from,
            to: searchData.to,
            departure: searchData.departure,
            selectedFlights: [existingOutbound, flightDetails]
          };

          if (searchData.return) {
            bookingData.return = searchData.return;
          }

          navigateWithBookingData(
            navigate,
            `/flight-booking-flow/${outboundId}`,
            bookingData,
            {
              state: {
                selectedFlight: existingOutbound,
                selectedFlights: [existingOutbound, flightDetails],
                searchParams: searchData,
                fromUrl: location.pathname + location.search
              }
            }
          );
          return;
        }
      }

      // One-way flight - navigate directly
      const bookingData = {
        flight_id: flightDetails.id,
        aircraft_id: flightDetails.aircraft_id,
        registration_number: flightDetails.registration_number,
        flight_number: flightDetails.flight_number,
        departure_airport: flightDetails.departure_airport,
        departure_date_time: flightDetails.departure?.datetime,
        arrival_airport: flightDetails.arrival_airport,
        arrival_date_time: flightDetails.arrival?.datetime,
        passengers: searchData.passengers || 1,
        routeType: searchData.tripType || 'One Way',
        from: searchData.from,
        to: searchData.to,
        departure: searchData.departure
      };

      navigateWithBookingData(
        navigate,
        `/flight-booking-flow/${flightDetails.id}`,
        bookingData,
        {
          state: {
            selectedFlight: flightDetails,
            searchParams: searchData,
            fromUrl: location.pathname + location.search
          }
        }
      );
    } catch (error) {
      console.error('Flight selection error:', error);
      toast.error('Failed to select flight. Please try again.');
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      lowestPax: false,
      fastestFlightTime: false,
      morning: false,
      afternoon: false,
      evening: false,
      sortBy: 'default'
    });
  };

  const handleNext = () => {
    const hasUnavailableSeats = selectedFlights.some(flight => flight.remainingSeat === 0);

    if (hasUnavailableSeats) {
      toast.error('There is no seat available on this select flight, please select other flight.');
      return;
    }

    // Navigate to flight booking flow for all trip types
    const flightId = selectedFlights[0]?.id;
    navigate(`/flight-booking-flow/${flightId}`, {
      state: {
        selectedFlights,
        totalPassengers,
        searchData,
        routeType: flightType === 'multi-city' ? 'Multicity' : flightType === 'return' ? 'Returns' : 'One Way'
      }
    });
  };

  return {
    // State
    outboundFlights,
    returnFlights,
    multiCitySegments,
    activeSegmentIndex,
    loading: loading || searchLoading,
    selectedFlights,
    activeFlightTab,
    filters,
    flightType,
    mode,
    totalPassengers,
    searchData,
    searchError,
    departureAirport,
    arrivalAirport,
    enrichedSegments,

    // Functions
    getDisplayFlights,
    handleFlightSelect,
    handleFilterChange,
    handleResetFilters,
    setActiveFlightTab,
    setActiveSegmentIndex,
    handleNext
  };
};
